import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import {
  RouterProvider,
  createRouter,
  parseSearchWith,
  stringifySearchWith,
} from "@tanstack/react-router";
import { parse, stringify } from "jsurl2";
import { toast } from "sonner";
import * as TanstackQuery from "./integrations/tanstack-query/root-provider";
import * as GoogleOauth from "./integrations/google-oauth/provider";
import "./styles.css";
import reportWebVitals from "./reportWebVitals.ts";
import { ThemeProvider } from "./components/themes/provider.tsx";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";
import { userInfoQueryOptions } from "./lib/queries/userInfo.ts";
import { polyfillToastConsole } from "./lib/utils/devConsoleToast.ts";
import { NotFound } from "./components/not-found/NotFound.tsx";
import { GtagProvider } from "./integrations/gtag/provider.tsx";
import {
  initGaConsentSettings,
  sendWebVitals,
} from "./integrations/gtag/api.ts";
import type { FieldsData } from "./types/stealthAiWriter.ts";
import type { GenerateArticleResult } from "./components/stealth-writer/GenerateArticle/ArticleGenerationService.ts";
import type { AudioAnalysisResult, VoiceFile } from "./lib/types/vexon.ts";

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(import.meta.env.DEV ? console.info : sendWebVitals);

// set consent settings on load because ga/gtag doesn't actually save them on their end
initGaConsentSettings();

if (
  import.meta.env.DEV &&
  import.meta.env.VITE_DEV_TOAST_ON_CONSOLE === "true"
) {
  window.addEventListener("error", (e) =>
    toast.error("Uncaught exception thrown:", { description: e.message }),
  );
  polyfillToastConsole();
}

const queryContext = TanstackQuery.getContext();

// prefetch userinfo as early as possible
queryContext.queryClient.prefetchQuery(userInfoQueryOptions);

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    ...queryContext,
  },
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,

  defaultViewTransition: true,

  parseSearch: parseSearchWith(parse),
  stringifySearch: stringifySearchWith(stringify),
  defaultNotFoundComponent: () => <NotFound />,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface HistoryState {
    // ai writer state
    fieldsData?: FieldsData;
    result?: GenerateArticleResult;
    parent_id?: string;
    // document redactor state
    documentRedactFile?: File;
    documentRedactResult?: {
      output_file_url: string;
      message: string;
    };

    // vexon state
    vexonResult?: AudioAnalysisResult;
    vexonAudio?: {
      verify: VoiceFile;
      reference?: VoiceFile;
    };
  }

  interface StaticDataRouteOption {
    title?: string;
  }
}
window.onbeforeunload = () => console.log("unloading....");

// Render the app
const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <ThemeProvider>
        <GoogleOauth.Provider>
          <TanstackQuery.Provider>
            <GtagProvider>
              <RouterProvider router={router} />
            </GtagProvider>
          </TanstackQuery.Provider>
        </GoogleOauth.Provider>
      </ThemeProvider>
    </StrictMode>,
  );
}
